// Public module — manifesto-style party comparison.
//
// Layout: pick two parties (defaults PL vs PN). Below: one row per
// life category that either party has content in, with the category
// label spanning across as a horizontal section break and each party's
// proposals listed in a column below it. Click any section header to
// collapse / expand that category for both parties at once.
//
// Sections start expanded; the user can collapse what they don't care
// about. On narrow viewports the two columns stack vertically (PL
// first, then PN) within each category section so the comparison flow
// is preserved.

import { useEffect, useMemo, useState } from 'react';
import ModuleSection from '../ModuleSection';
import { useProposals } from '../../lib/useProposals';
import { resolveSectionProps, useSectionText } from '../../lib/useSectionText';
import type { ProposalRow } from '../../db/types';
import { CATEGORIES, categoryFor, type Category } from './categories';
import './Proposals.css';

interface PartyMeta {
  key: string;
  label: string;
  shortLabel: string;
  color: string;
}

const PARTY_META: PartyMeta[] = [
  { key: 'PL', label: 'Labour (PL)', shortLabel: 'PL', color: 'var(--party-pl)' },
  { key: 'PN', label: 'Nationalist (PN)', shortLabel: 'PN', color: 'var(--party-pn)' },
  { key: 'AD', label: 'AD/ADPD', shortLabel: 'AD', color: 'var(--party-ad)' },
  { key: 'Momentum', label: 'Momentum', shortLabel: 'Mom', color: 'var(--party-momentum)' },
  { key: 'Imperium', label: 'Imperium', shortLabel: 'Imp', color: 'var(--party-imperium)' },
  { key: 'IND', label: 'Independent', shortLabel: 'IND', color: 'var(--party-ind)' },
];

const DEFAULT_LEFT = 'PL';
const DEFAULT_RIGHT = 'PN';

interface TaggedProposal extends ProposalRow {
  category: string;
}

// How many category rows to show before "Show all" expands the rest.
const VISIBLE_CATEGORIES_COLLAPSED = 2;

export default function ProposalsModule() {
  const { proposals, isLoading, error } = useProposals();
  const sectionText = useSectionText('proposals');
  const [leftParty, setLeftParty] = useState<string>(DEFAULT_LEFT);
  const [rightParty, setRightParty] = useState<string>(DEFAULT_RIGHT);
  // Collapsed category keys (shared across both columns so a click on
  // either party's header collapses the row as a whole).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Section-wide collapse: only the first N categories are rendered
  // until the editor expands. Reset to collapsed each visit so first-
  // load impressions aren't a wall of categories.
  const [showAllCategories, setShowAllCategories] = useState(false);

  function toggleCollapsed(catKey: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(catKey)) next.delete(catKey);
      else next.add(catKey);
      return next;
    });
  }

  // Tag every proposal with its derived category once.
  const tagged = useMemo<TaggedProposal[]>(
    () =>
      proposals.map((p) => ({
        ...p,
        // ADPD folds into AD so we don't have to keep them separate.
        party: p.party === 'ADPD' ? 'AD' : p.party,
        category: categoryFor(p.topic),
      })),
    [proposals],
  );

  // Bucket by (party, category).
  const buckets = useMemo(() => {
    const map = new Map<string, TaggedProposal[]>();
    const key = (party: string, cat: string) => `${party}::${cat}`;
    for (const p of tagged) {
      const k = key(p.party, p.category);
      const list = map.get(k) ?? [];
      list.push(p);
      map.set(k, list);
    }
    return {
      get: (party: string, cat: string) => map.get(key(party, cat)) ?? [],
      partiesPresent: () => {
        const set = new Set<string>();
        for (const k of map.keys()) set.add(k.split('::')[0]);
        return set;
      },
    };
  }, [tagged]);

  // Any party that has at least one proposal — used to populate the
  // dropdowns and avoid offering empty options.
  const partiesPresent = useMemo(() => buckets.partiesPresent(), [buckets]);
  const partyOptions = useMemo(
    () => PARTY_META.filter((m) => partiesPresent.has(m.key)),
    [partiesPresent],
  );

  // If the default parties have no content, switch to the first two
  // parties that do (defensive — rarely needed).
  useEffect(() => {
    if (partyOptions.length === 0) return;
    if (!partyOptions.find((p) => p.key === leftParty)) {
      setLeftParty(partyOptions[0].key);
    }
    if (
      !partyOptions.find((p) => p.key === rightParty) ||
      rightParty === leftParty
    ) {
      const next = partyOptions.find((p) => p.key !== leftParty);
      if (next) setRightParty(next.key);
    }
  }, [partyOptions, leftParty, rightParty]);

  // Categories with at least one proposal in either side, in the order
  // declared in CATEGORIES.
  const visibleCategories = useMemo(
    () =>
      CATEGORIES.filter((c) => {
        return (
          buckets.get(leftParty, c.key).length > 0 ||
          buckets.get(rightParty, c.key).length > 0
        );
      }),
    [buckets, leftParty, rightParty],
  );

  const leftMeta = PARTY_META.find((m) => m.key === leftParty) ?? PARTY_META[0];
  const rightMeta = PARTY_META.find((m) => m.key === rightParty) ?? PARTY_META[1];

  return (
    <ModuleSection
      {...resolveSectionProps(
        {
          number: '04',
          kicker: "The Two Parties'",
          title: 'Proposals',
          intro: 'What the parties have proposed so far. Updated daily.',
        },
        sectionText,
      )}
      id="proposals"
    >
      {error && (
        <p className="proposals__error">
          Couldn&apos;t load proposals right now. Refresh to try again.
        </p>
      )}

      {!error && isLoading && proposals.length === 0 && (
        <p className="proposals__empty">Loading proposals…</p>
      )}

      {!error && !isLoading && proposals.length === 0 && (
        <div className="proposals__empty">
          <p>
            No proposals published yet. The editor is still reviewing the
            first batch from coverage; check back soon.
          </p>
        </div>
      )}

      {proposals.length > 0 && partyOptions.length >= 2 && (
        <>
          <header className="proposals__col-headers">
            <select
              className="proposals__col-header proposals__col-picker"
              value={leftParty}
              onChange={(e) => setLeftParty(e.target.value)}
              style={{ borderTopColor: leftMeta.color, color: leftMeta.color }}
              aria-label="Pick party for left column"
            >
              {partyOptions.map((m) => (
                <option key={m.key} value={m.key} disabled={m.key === rightParty}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              className="proposals__col-header proposals__col-picker"
              value={rightParty}
              onChange={(e) => setRightParty(e.target.value)}
              style={{ borderTopColor: rightMeta.color, color: rightMeta.color }}
              aria-label="Pick party for right column"
            >
              {partyOptions.map((m) => (
                <option key={m.key} value={m.key} disabled={m.key === leftParty}>
                  {m.label}
                </option>
              ))}
            </select>
          </header>

          <div className="proposals__compare">
            {visibleCategories.length === 0 && (
              <p className="proposals__empty">
                No published proposals yet for either of these parties.
              </p>
            )}
            {(showAllCategories
              ? visibleCategories
              : visibleCategories.slice(0, VISIBLE_CATEGORIES_COLLAPSED)
            ).map((cat) => (
              <CategoryRow
                key={cat.key}
                category={cat}
                left={buckets.get(leftParty, cat.key)}
                right={buckets.get(rightParty, cat.key)}
                leftMeta={leftMeta}
                rightMeta={rightMeta}
                isCollapsed={collapsed.has(cat.key)}
                onToggle={() => toggleCollapsed(cat.key)}
              />
            ))}
            {visibleCategories.length > VISIBLE_CATEGORIES_COLLAPSED && (
              <button
                type="button"
                className="proposals__show-all"
                onClick={() => setShowAllCategories((v) => !v)}
                aria-expanded={showAllCategories}
              >
                {showAllCategories
                  ? 'Show fewer categories'
                  : `Show all ${visibleCategories.length} categories`}
                <span
                  className={`proposals__show-all-chev${
                    showAllCategories ? ' is-open' : ''
                  }`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>
            )}
          </div>
        </>
      )}
    </ModuleSection>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function CategoryRow({
  category,
  left,
  right,
  leftMeta,
  rightMeta,
  isCollapsed,
  onToggle,
}: {
  category: Category;
  left: ProposalRow[];
  right: ProposalRow[];
  leftMeta: PartyMeta;
  rightMeta: PartyMeta;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <section className="proposals__cat-row" aria-label={category.label}>
      <button
        type="button"
        className="proposals__cat-bar"
        aria-expanded={!isCollapsed}
        onClick={onToggle}
      >
        <span className="proposals__cat-bar-icon" aria-hidden="true">
          {category.icon}
        </span>
        <span className="proposals__cat-bar-label">{category.label}</span>
        <span className="proposals__cat-bar-counts">
          {leftMeta.shortLabel} <strong>{left.length}</strong> ·{' '}
          {rightMeta.shortLabel} <strong>{right.length}</strong>
        </span>
        <span
          className={`proposals__cat-bar-chev${isCollapsed ? ' is-collapsed' : ''}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>
      {!isCollapsed && (
        <div className="proposals__cat-body">
          <ProposalList
            proposals={left}
            accent={leftMeta.color}
            partyLabel={leftMeta.label}
            emptyLabel={`${leftMeta.shortLabel} hasn't proposed anything on this yet.`}
          />
          <ProposalList
            proposals={right}
            accent={rightMeta.color}
            partyLabel={rightMeta.label}
            emptyLabel={`${rightMeta.shortLabel} hasn't proposed anything on this yet.`}
          />
        </div>
      )}
    </section>
  );
}

const COLLAPSED_LIMIT = 2;

function ProposalList({
  proposals,
  accent,
  partyLabel,
  emptyLabel,
}: {
  proposals: ProposalRow[];
  accent: string;
  partyLabel: string;
  emptyLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (proposals.length === 0) {
    return (
      <div className="proposal-empty-cell">
        <span className="proposal-list__party-label" style={{ color: accent }}>
          {partyLabel}
        </span>
        <p className="proposal-empty-cell__text">{emptyLabel}</p>
      </div>
    );
  }
  const visible = expanded ? proposals : proposals.slice(0, COLLAPSED_LIMIT);
  const hidden = proposals.length - visible.length;
  return (
    <div className="proposal-list">
      <span className="proposal-list__party-label" style={{ color: accent }}>
        {partyLabel}
      </span>
      {visible.map((p) => (
        <ProposalCard key={p.id} proposal={p} accent={accent} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="proposal-list__more"
          onClick={() => setExpanded(true)}
          style={{ borderColor: accent, color: accent }}
        >
          + {hidden} more
        </button>
      )}
      {expanded && proposals.length > COLLAPSED_LIMIT && (
        <button
          type="button"
          className="proposal-list__more proposal-list__more--less"
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </div>
  );
}

function ProposalCard({ proposal, accent }: { proposal: ProposalRow; accent: string }) {
  return (
    <article className="proposal-card" style={{ borderLeftColor: accent }}>
      {proposal.topic && (
        <div className="proposal-card__topic" style={{ color: accent }}>
          {proposal.topic}
        </div>
      )}
      <h4 className="proposal-card__title">{proposal.title}</h4>
      {proposal.description && (
        <p className="proposal-card__description">{proposal.description}</p>
      )}
      <ProposalDetailsList details={proposal.details} />
      <footer className="proposal-card__footer">
        {proposal.announced_date && (
          <time className="proposal-card__date" dateTime={proposal.announced_date}>
            {formatDate(proposal.announced_date)}
          </time>
        )}
        {proposal.status === 'withdrawn' && (
          <span className="proposal-card__status proposal-card__status--withdrawn">
            withdrawn
          </span>
        )}
        {proposal.status === 'clarified' && (
          <span className="proposal-card__status proposal-card__status--clarified">
            clarified
          </span>
        )}
        {proposal.source_url && (
          <a
            href={proposal.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="proposal-card__source"
          >
            Source →
          </a>
        )}
      </footer>
    </article>
  );
}

// Field label + ordering for the structured policy details. Only fields
// the editor / Tidy agent has filled are shown — empty fields collapse.
const DETAIL_LABELS: Array<[keyof NonNullable<ProposalRow['details']>, string]> = [
  ['timeline', 'Timeline'],
  ['cost', 'Cost'],
  ['funding', 'Funding'],
  ['revenue', 'Revenue'],
  ['beneficiaries', 'Beneficiaries'],
  ['scope', 'Scope'],
  ['mechanism', 'Mechanism'],
  ['conditions', 'Conditions'],
];

function ProposalDetailsList({ details }: { details: ProposalRow['details'] }) {
  if (!details) return null;
  const filled = DETAIL_LABELS.filter(([k]) => {
    const v = details[k];
    return typeof v === 'string' && v.trim().length > 0;
  });
  if (filled.length === 0) return null;
  return (
    <dl className="proposal-card__details">
      {filled.map(([k, label]) => (
        <div key={k} className="proposal-card__detail">
          <dt>{label}</dt>
          <dd>{details[k]}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
